// Panel de modération — signalements des utilisateurs.
// Groupe les reports par interaction. Chaque carte permet :
//   - de rejeter le signalement (dismiss)
//   - de supprimer le commentaire/réaction (soft-delete)
//   - de bannir l'auteur (invalide son token, bloque la re-connexion)
import React, { useEffect, useState, useCallback } from "react";
import {
	adminReports,
	adminReportDismiss,
	adminInteractionDelete,
	adminUserBan,
	adminUserUnban,
} from "../services/authService";

const FILTER_LABELS = {
	pending: "En attente",
	reviewed: "Traités",
	all: "Tous",
};

const STATUT_LABELS = {
	0: { label: "En attente", color: "#e57373" },
	1: { label: "Rejeté", color: "var(--ink-soft)" },
	2: { label: "Action prise", color: "#4ade80" },
};

const TYPE_EMOJI = {
	muscle: "💪",
	heart: "❤️",
	shocked: "😳",
	laugh: "😂",
	comment: "💬",
	// Legacy codes (peuvent apparaître sur d'anciennes réactions non re-labellisées)
	bravo: "❤️",
	fire: "😳",
};

function formatDateTime(v) {
	if (!v) return "—";
	const d = new Date(v);
	if (isNaN(d)) return "—";
	return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function BanModal({ user, onClose, onBanned }) {
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const handleBan = async () => {
		setSaving(true);
		setError("");
		const r = await adminUserBan(user.id, reason.trim());
		setSaving(false);
		if (r.success) onBanned?.();
		else setError(r.error || "Erreur");
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
				<div className="mens-modal-header">
					<h3>Bannir un utilisateur</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				<p style={{ margin: 0, color: "var(--ink-soft)", fontSize: 14 }}>
					Utilisateur : <strong style={{ color: "var(--ink)" }}>{user.prenom} {user.nom}</strong>
					<br />
					<span style={{ fontSize: 12 }}>{user.email}</span>
				</p>

				<div className="group-settings-section" style={{ marginTop: 18 }}>
					<label className="group-settings-label" htmlFor="ban-reason">
						Motif (visible dans le journal d'audit uniquement)
					</label>
					<textarea
						id="ban-reason"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						maxLength={500}
						rows={3}
						placeholder="Spam / harcèlement / contenu inapproprié…"
						className="admin-broadcast-input"
					/>
				</div>

				<div style={{ background: "rgba(229,115,115,0.08)", border: "1px solid rgba(229,115,115,0.25)", borderRadius: 10, padding: 10, fontSize: 12, color: "var(--ink-soft)", marginTop: 14 }}>
					Le token de session sera invalidé — l'utilisateur sera déconnecté et ne pourra plus se reconnecter tant que le ban est actif.
				</div>

				{error && <div className="mens-modal-error">{error}</div>}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={saving}>Annuler</button>
					<button className="mens-btn danger" onClick={handleBan} disabled={saving}>
						{saving ? "Bannissement…" : "Bannir"}
					</button>
				</div>
			</div>
		</div>
	);
}

export default function AdminModerationPanel() {
	const [reports, setReports] = useState([]);
	const [filter, setFilter] = useState("pending");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [banTarget, setBanTarget] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError("");
		const r = await adminReports({ filter, page });
		setLoading(false);
		if (r.success) {
			setReports(r.reports || []);
			setTotal(r.total || 0);
			setTotalPages(r.totalPages || 1);
		} else {
			setError(r.error || "Erreur");
		}
	}, [filter, page]);

	useEffect(() => { load(); }, [load]);

	const handleDismiss = async (idInteraction) => {
		if (!window.confirm("Marquer ce signalement comme non problématique ?")) return;
		const r = await adminReportDismiss(idInteraction);
		if (r.success) load();
		else alert(r.error || "Erreur");
	};

	const handleDelete = async (idInteraction) => {
		if (!window.confirm("Supprimer définitivement ce commentaire ?")) return;
		const r = await adminInteractionDelete(idInteraction);
		if (r.success) load();
		else alert(r.error || "Erreur");
	};

	const handleUnban = async (userId) => {
		if (!window.confirm("Débannir cet utilisateur ?")) return;
		const r = await adminUserUnban(userId);
		if (r.success) load();
		else alert(r.error || "Erreur");
	};

	return (
		<div>
			<div className="admin-users-toolbar" style={{ marginBottom: 12 }}>
				<div className="admin-users-filters">
					{Object.entries(FILTER_LABELS).map(([k, l]) => (
						<button
							key={k}
							type="button"
							className={`admin-filter-chip ${filter === k ? "selected" : ""}`}
							onClick={() => { setPage(1); setFilter(k); }}
						>
							{l}
						</button>
					))}
				</div>
				<span style={{ color: "var(--ink-soft)", fontSize: 14 }}>
					{total} signalement{total > 1 ? "s" : ""}
				</span>
			</div>

			{error && <div className="admin-error">{error}</div>}
			{loading && <div className="admin-loading">Chargement…</div>}

			{!loading && reports.length === 0 && (
				<div className="admin-empty">Aucun signalement à afficher.</div>
			)}

			{!loading && reports.length > 0 && (
				<div className="moderation-list">
					{reports.map((r) => {
						const statut = STATUT_LABELS[r.statut] || STATUT_LABELS[0];
						const isComment = r.typeCode === "comment";
						return (
							<div key={r.idInteraction} className={`moderation-card ${r.supprime ? "deleted" : ""}`}>
								<div className="moderation-card-head">
									<div className="moderation-card-type">
										<span className="moderation-type-icon">{TYPE_EMOJI[r.typeCode] || "?"}</span>
										<span className="moderation-type-label">{r.typeLibelle}</span>
									</div>
									<div className="moderation-card-badges">
										<span className="moderation-badge" style={{ color: statut.color, borderColor: statut.color + "80" }}>
											{statut.label}
										</span>
										<span className="moderation-badge count">
											{r.reportCount} signalement{r.reportCount > 1 ? "s" : ""}
										</span>
										{r.supprime && <span className="moderation-badge deleted">Supprimé</span>}
									</div>
								</div>

								{isComment && r.commentaire && (
									<blockquote className="moderation-content">
										« {r.commentaire} »
									</blockquote>
								)}
								{!isComment && (
									<div className="moderation-content muted">
										Réaction sur la série #{r.idSerie}
									</div>
								)}

								<div className="moderation-meta">
									<div>
										<span className="moderation-meta-label">Auteur</span>
										<strong>
											{r.offender.prenom} {r.offender.nom}
											{r.offender.banned && <span className="moderation-ban-tag">banni</span>}
										</strong>
										<div className="moderation-email">{r.offender.email} · #{r.offender.id}</div>
									</div>
									<div>
										<span className="moderation-meta-label">Signalé</span>
										<strong>{formatDateTime(r.lastReport)}</strong>
										{r.reportCount > 1 && (
											<div className="moderation-email">Premier: {formatDateTime(r.firstReport)}</div>
										)}
									</div>
									<div>
										<span className="moderation-meta-label">Publié</span>
										<strong>{formatDateTime(r.dateInteraction)}</strong>
									</div>
								</div>

								<div className="moderation-actions">
									{r.statut === 0 && !r.supprime && (
										<>
											<button
												className="mens-btn ghost small"
												onClick={() => handleDismiss(r.idInteraction)}
											>
												Rejeter le signalement
											</button>
											<button
												className="mens-btn danger small"
												onClick={() => handleDelete(r.idInteraction)}
											>
												Supprimer le contenu
											</button>
										</>
									)}
									{r.offender.banned ? (
										<button
											className="mens-btn ghost small"
											onClick={() => handleUnban(r.offender.id)}
										>
											Débannir
										</button>
									) : (
										<button
											className="mens-btn danger small"
											onClick={() => setBanTarget(r.offender)}
										>
											Bannir l'auteur
										</button>
									)}
								</div>
							</div>
						);
					})}

					<div className="admin-users-pagination">
						<span>Page {page} / {totalPages}</span>
						<div>
							<button
								className="mens-btn ghost small"
								disabled={page <= 1}
								onClick={() => setPage(page - 1)}
							>
								← Précédent
							</button>
							<button
								className="mens-btn ghost small"
								disabled={page >= totalPages}
								onClick={() => setPage(page + 1)}
								style={{ marginLeft: 6 }}
							>
								Suivant →
							</button>
						</div>
					</div>
				</div>
			)}

			{banTarget && (
				<BanModal
					user={banTarget}
					onClose={() => setBanTarget(null)}
					onBanned={() => { setBanTarget(null); load(); }}
				/>
			)}
		</div>
	);
}
